// Aug 2026 v3 · Viatris phase journey — simplified two-section structure.
// "Phase 1 — COMPLETED" (compact historical card) + "Phase 2 — ACTIVE"
// (prominent operational section). Used by the internal Workbench
// (mode="admin": actions + table filtering) and the client dashboard
// (mode="client": read-only).
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { ChevronRight, CheckCircle2, Loader2, Users, Sparkles, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_LABELS = { 1: 'Eligible for AI', 2: 'AI Assessed', 3: 'Committee Review', 4: 'Client Shortlisted', 5: 'Availability / BD' };
const P1_LABELS = { 1: 'Eligible Assets', 2: 'AI Assessed', 3: 'Committee Review', 4: 'Client Shortlisted' };
const CB_LABELS = { pending: 'Pending', under_review: 'Under Review', scored: 'Scored' };
const REC_CATEGORIES = ['Strategic Priority', 'High Priority', 'Medium Priority', 'Monitor'];

const slug = (s) => s.replace(/\s+/g, '-').toLowerCase();

export const ViatrisJourneyPanel = ({ mode = 'admin', token = '', journeyFilterKey = null, onJourneyFilter, onDataChanged }) => {
  const [phases, setPhases] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [poolModal, setPoolModal] = useState(false);
  const [poolAssets, setPoolAssets] = useState([]);
  const [builder, setBuilder] = useState(null); // create-phase preview
  const [closer, setCloser] = useState(null);   // {phase, rows, carry:Set}
  const [p1Open, setP1Open] = useState(false);
  const [job, setJob] = useState(null);
  const [modal, setModal] = useState(null); // 'committee'
  const [cats, setCats] = useState(['Strategic Priority', 'High Priority']);
  const [candidates, setCandidates] = useState([]);
  const [checked, setChecked] = useState([]);
  const [preview, setPreview] = useState(null);
  const [showAssetPick, setShowAssetPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const url = mode === 'client' ? `${API}/client/viatris/${token}/journey` : `${API}/viatris/journey/overview`;
      const r = await axios.get(url, { withCredentials: true });
      setPhases(r.data.phases || []);
    } catch { setPhases([]); }
    if (mode === 'admin') {
      try {
        const a = await axios.get(`${API}/viatris/journey/allocation`, { withCredentials: true });
        setAlloc(a.data);
      } catch { /* strip hidden on failure */ }
    }
  }, [mode, token]);

  const p1 = (phases || []).find((p) => p.phase === 1);
  const active = (phases || []).filter((p) => p.phase >= 2 && p.status === 'active').slice(-1)[0] || null;
  const activeN = active?.phase || 2;
  const stg = (p, n) => p?.stages?.find((s) => s.stage === n)?.count ?? 0;

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  const pollJob = useCallback((phaseN) => {
    stopPoll();
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/viatris/cohorts/${encodeURIComponent(`Phase ${phaseN}`)}/assessment-status`, { withCredentials: true });
        const d = r.data || {};
        if (d.status === 'preparing' || d.status === 'running') { setJob(d); return; }
        const wasRunning = !!pollRef.current;
        stopPoll(); setJob(null);
        if (wasRunning) {
          if (d.status === 'done' || d.status === 'completed') toast.success(`AI assessment finished — ${d.completed || 0} asset(s) assessed`);
          await load(); onDataChanged && onDataChanged();
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 5000);
  }, [load, onDataChanged]);

  useEffect(() => {
    load();
    if (mode === 'admin') {
      // Resume progress strip if a phase batch is already in flight.
      (async () => {
        try {
          const r = await axios.get(`${API}/viatris/cohorts/${encodeURIComponent('Phase 2')}/assessment-status`, { withCredentials: true });
          if (r.data?.status === 'preparing' || r.data?.status === 'running') pollJob(2);
        } catch { /* noop */ }
      })();
    }
    return stopPoll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('viatris:journey-reload', load);
    return () => window.removeEventListener('viatris:journey-reload', load);
  }, [load]);

  // Default table context — when an active phase exists and no other filter
  // is set, the table opens on "Phase N Assets · Eligible for AI" so the
  // header always explains what the rows are.
  const autoApplied = useRef(false);
  useEffect(() => {
    if (autoApplied.current || mode !== 'admin' || !onJourneyFilter || !phases) return;
    const act = phases.filter((p) => p.phase >= 2 && p.status === 'active').slice(-1)[0];
    if (!act || journeyFilterKey) return;
    if (new URLSearchParams(window.location.search).get('rec')) return;
    autoApplied.current = true;
    const c = act.stages?.find((s) => s.stage === 1)?.count ?? 0;
    if (c > 0) click(act.phase, 'eligible', `Stage 1 · ${STAGE_LABELS[1]}`, c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phases]);

  const click = async (phaseN, metric, label, count) => {
    if (mode !== 'admin' || !onJourneyFilter) return;
    const key = `${phaseN}:${metric}`;
    if (journeyFilterKey === key) { onJourneyFilter(null); return; }
    try {
      const r = await axios.get(`${API}/viatris/journey/assets`, { params: { cohort: phaseN, metric }, withCredentials: true });
      onJourneyFilter({
        key,
        phase: phaseN,
        metric,
        label: `Cohort ${phaseN} — ${r.data.count} ${label}`,
        count: r.data.count,
        ids: (r.data.assets || []).map((a) => a.id),
        assetIds: (r.data.assets || []).map((a) => a.asset_id).filter(Boolean),
      });
    } catch { toast.error('Could not load the assets behind this count'); }
  };

  const runAI = async () => {
    const n = active?.awaiting_ai ?? 0;
    if (!n) return;
    if (!window.confirm(
      `Run AI Assessment on the ${n} Cohort ${activeN} asset(s)?\n\n` +
      `This runs ONLY on the ${n} assets that are explicit Cohort ${activeN} members.\n` +
      `Each asset goes through the Perplexity + Claude 8-step pipeline.\n` +
      `Expected duration: ~${Math.max(5, Math.round((n / 3) * 75 / 60))} min.`
    )) return;
    try {
      await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(`Phase ${activeN}`)}/generate-batch` +
        `?limit=300&only_unassessed=true&cohort_scope=engagement&screening_eligible_only=false&phase_id=${activeN}`,
        {}, { withCredentials: true, timeout: 45000 });
      toast.info('Batch accepted — progress appears below.', { duration: 6000 });
      setJob({ status: 'preparing', total: n, completed: 0 });
      pollJob(activeN);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Run AI failed'); }
  };

  const openCommittee = async () => {
    setModal('committee'); setPreview(null); setChecked([]); setShowAssetPick(false);
    try {
      const r = await axios.get(`${API}/viatris/journey/assets`, { params: { cohort: activeN, metric: 'committee_candidates' }, withCredentials: true });
      setCandidates(r.data.assets || []);
    } catch { setCandidates([]); }
    previewCommittee(cats, []);
  };
  const previewCommittee = async (categories, assetIds) => {
    setCats(categories);
    try {
      const r = await axios.post(`${API}/viatris/journey/send-to-committee`,
        { cohort_number: activeN, categories, asset_ids: assetIds, confirm: false }, { withCredentials: true });
      setPreview(r.data);
    } catch { setPreview({ selected_count: 0, sample: [] }); }
  };
  const confirmCommittee = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/viatris/journey/send-to-committee`,
        { cohort_number: activeN, categories: cats, asset_ids: checked, confirm: true }, { withCredentials: true });
      toast.success(`${r.data.moved} asset${r.data.moved === 1 ? '' : 's'} moved to Committee Review (Pending)`);
      setModal(null); await load(); onDataChanged && onDataChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const exportUrl = (phaseN, metric) =>
    `${API}/viatris/journey/export?cohort=${phaseN}&metric=${encodeURIComponent(metric)}`;

  const ExportMenu = ({ phase }) => {
    const [open, setOpen] = useState(false);
    useEffect(() => {
      if (!open) return;
      const onKey = (e) => e.key === 'Escape' && setOpen(false);
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [open]);
    if (!admin || !phase) return null;
    const s = (k) => phase.stages?.find((x) => x.stage === k)?.count ?? 0;
    const items = [
      { m: 'eligible', l: phase.phase === 1 ? `Eligible Assets — ${s(1)}` : `Starting Cohort — ${s(1)}` },
      { m: 'ai_assessed', l: `AI-Assessed — ${s(2)}` },
      ...(phase.recommendations || []).map((r) => ({ m: `rec:${r.label}`, l: `${r.label} — ${r.count}` })),
      { m: 'committee', l: `Committee Review — ${s(3)}` },
      { m: 'shortlisted', l: `Client Shortlist — ${s(4)}` },
      { m: 'team_approved', l: `Team Approved — ${s(5)}` },
      { m: 'responded', l: `Responded — ${phase.responded_count ?? 0}` },
      ...(phase.phase === 1 && (phase.carried_forward ?? 0) > 0
        ? [{ m: 'carried', l: `Carried Forward — ${phase.carried_forward}` }] : []),
    ];
    return (
      <div className="relative">
        <button onClick={() => setOpen((v) => !v)} data-testid={`phase${phase.phase}-export-menu-btn`}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 transition">
          <Download className="w-3 h-3" /> Export <ChevronDown className="w-3 h-3 opacity-60" />
        </button>
        {open && (
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
            <div className="absolute right-0 top-full mt-1 z-[70] w-64 bg-white border border-slate-200 rounded-md shadow-xl py-1" data-testid={`phase${phase.phase}-export-menu`}>
              <div className="px-3 py-1 text-[9px] uppercase tracking-wide font-bold text-slate-400">Cohort {phase.phase} Exports (Excel)</div>
              {items.filter((it) => !it.l.endsWith('— 0')).map((it) => (
                <button key={it.m} data-testid={`phase${phase.phase}-export-${slug(it.m.replace('rec:', ''))}`}
                  className="w-full text-left px-3 py-1.5 text-[11px] text-slate-700 hover:bg-violet-50"
                  onClick={() => { window.open(exportUrl(phase.phase, it.m), '_blank'); toast.success(`Downloading Cohort ${phase.phase} · ${it.l}`); setOpen(false); }}>
                  {it.l}
                </button>
              ))}
              {phase.status === 'active' && (
                <>
                  <div className="border-t border-slate-100 my-1" />
                  <button data-testid="internal-master-export-btn"
                    className="w-full text-left px-3 py-1.5 text-[11px] text-red-700 hover:bg-red-50"
                    onClick={() => { window.open(exportUrl(0, 'all_internal'), '_blank'); toast.info('Downloading INTERNAL master export — do not share with client'); setOpen(false); }}>
                    <span className="font-bold text-[9px] bg-red-100 border border-red-200 rounded px-1 mr-1.5">INTERNAL</span>{' '}
                    Master Export — All Cohorts
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  if (phases === null) return null;
  if (!p1) return null;

  const admin = mode === 'admin';
  const carried = p1.carried_forward ?? p1.awaiting_ai ?? 0;
  const p1Metrics = { 1: 'eligible', 2: 'ai_assessed', 3: 'committee', 4: 'shortlisted' };
  const p2Metrics = { 1: 'eligible', 2: 'ai_assessed', 3: 'committee', 4: 'shortlisted', 5: 'stage5' };
  const activeCls = (key) => (admin && journeyFilterKey === key ? 'ring-2 ring-violet-500 border-violet-500' : '');
  const cbTotal = (cb) => (cb ? cb.pending + cb.under_review + cb.scored : 0);

  const Stat = ({ phaseN, metric, count, label, sub, big }) => {
    const key = `${phaseN}:${metric}`;
    const Tag = admin ? 'button' : 'div';
    return (
      <Tag
        onClick={admin ? () => click(phaseN, metric, label, count) : undefined}
        title={metric === 'team_approved'
          ? "Firm client milestone — the client's formal decision to pursue. Availability outcomes (Available / Not Available / Awaiting Response) never overwrite this status."
          : metric === 'responded'
            ? 'Companies that replied to our outreach (marked Responded: Yes). Independent of Team Approved / Shortlisted / availability.'
            : undefined}
        data-testid={`phase${phaseN}-${slug(metric)}-stat`}
        className={`text-left rounded-lg border bg-white px-3 py-2 transition ${big ? 'flex-1 min-w-[120px]' : 'min-w-[105px]'} ${admin ? 'hover:border-violet-400 cursor-pointer' : ''} ${metric === 'responded' ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'} ${activeCls(key)}`}
      >
        <div className={`${big ? 'text-2xl' : 'text-xl'} font-bold text-slate-900`}>{Number(count).toLocaleString()}</div>
        <div className="text-[11px] font-medium text-slate-600 mt-0.5">{label}</div>
        {sub}
      </Tag>
    );
  };

  const Arrow = () => <div className="self-center text-slate-300"><ChevronRight className="w-4 h-4" /></div>;

  return (
    <div className="mt-3 space-y-3" data-testid="viatris-journey-panel">

      {/* ── LIVE CHINA UNIVERSE — compact summary (dynamic, never phase-bound) ── */}
      {admin && alloc && (
        <div className="border border-slate-200 rounded-lg bg-white px-3 py-2.5 flex items-center gap-2 flex-wrap" data-testid="live-universe-strip">
          <span className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Live China Universe</span>
          <span className="text-[12px] text-slate-700" data-testid="live-universe-summary">
            <span className="font-bold text-slate-900">{Number((alloc.stages || [])[0]?.count ?? 0).toLocaleString()}</span> total assets
            {' · '}<span className="font-bold text-emerald-700" data-testid="currently-eligible-chip">{alloc.eligible}</span> currently eligible
            {' · '}<span className="font-semibold">{alloc.assigned_of_eligible}</span> assigned to cohorts
            {' · '}<span className={`font-semibold ${alloc.next_pool > 0 ? 'text-sky-700' : ''}`}>{alloc.next_pool}</span> awaiting next cohort
          </span>
          <button data-testid="view-screening-details-btn"
            className="ml-auto text-[11px] font-medium text-slate-500 hover:text-slate-800 underline decoration-dotted"
            onClick={() => {
              const el = document.getElementById('screening-details-block');
              if (el) { el.open = !el.open; if (el.open) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
            }}>
            View Screening Details
          </button>
        </div>
      )}

      {/* ── NEXT PHASE POOL — prominent only when actionable ── */}
      {admin && alloc && (alloc.next_pool > 0 || !active) && (
        <div className={`border rounded-lg p-3 ${alloc.next_pool > 0 ? 'border-sky-300 bg-sky-50/60' : 'border-slate-200 bg-slate-50/50'}`} data-testid="next-cohort-pool-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[12px] font-bold text-slate-900">Next Cohort Pool — {alloc.next_pool} asset{alloc.next_pool === 1 ? '' : 's'}</span>
            <span className="text-[11px] text-slate-500" data-testid="cohort-allocation-strip">
              {alloc.next_pool > 0 ? `${alloc.next_pool} new eligible asset${alloc.next_pool === 1 ? '' : 's'} awaiting the next cohort` : 'No new eligible assets awaiting assignment'}
            </span>
            <div className="ml-auto flex items-center gap-1.5">
              {alloc.next_pool > 0 && (
                <>
                  <button data-testid="pool-view-btn"
                    className="text-[11px] px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/viatris/journey/assets`, { params: { cohort: 'pool', metric: 'next_pool' }, withCredentials: true });
                        setPoolAssets(r.data.assets || []); setPoolModal(true);
                      } catch { toast.error('Could not load pool assets'); }
                    }}>View Assets</button>
                  <button data-testid="pool-export-btn"
                    className="text-[11px] px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                    onClick={() => window.open(`${API}/viatris/journey/export?cohort=0&metric=next_pool`, '_blank')}>Export</button>
                </>
              )}
              <button data-testid="create-next-phase-btn"
                className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40"
                disabled={!!active}
                title={active ? `Close Cohort ${activeN} first — a new cohort can only be created when no cohort is active.` : ''}
                onClick={async () => {
                  try {
                    const r = await axios.post(`${API}/viatris/journey/create-phase`, { confirm: false }, { withCredentials: true });
                    setBuilder({ ...r.data, include_carry: true, include_pool: true });
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot create phase'); }
                }}>Create Next Cohort</button>
            </div>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            Eligible assets identified after the current cohort was created, awaiting inclusion in the next review cohort. They are never mixed into an active cohort automatically.
          </div>
        </div>
      )}

      {/* ── PHASE 1 — COMPLETED ── */}
      {p1.status === 'completed' ? (
        <div className="border border-slate-200 rounded-lg bg-slate-50/70 p-3.5" data-testid="phase1-completed-card">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[13px] font-bold text-slate-900">Cohort 1</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="phase1-completed-badge">
              <CheckCircle2 className="w-3 h-3" /> Completed{p1.completed_at ? ` · ${p1.completed_at.slice(0, 10)}` : ''}
            </span>
            <button onClick={() => setP1Open((v) => !v)} data-testid="view-phase1-details-btn"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-slate-800 transition">
              View Cohort 1 Details {p1Open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            <ExportMenu phase={p1} />
          </div>
          <div className="flex items-stretch gap-1.5 flex-wrap">
            {(p1.stages || [{ stage: 1 }]).filter((s) => s.stage <= 5 || s.count > 0).map((s, i, arr) => (
              <React.Fragment key={s.stage}>
                <Stat phaseN={1} metric={s.metric || p1Metrics[s.stage]} count={s.count ?? 0}
                  label={s.stage <= 4 ? P1_LABELS[s.stage] : s.label} />
                {i < arr.length - 1 && <Arrow />}
              </React.Fragment>
            ))}
            {/* Aug 2026 · Responded — outreach replies, independent dimension */}
            <Arrow />
            <Stat phaseN={1} metric="responded" count={p1.responded_count ?? 0} label="Responded" />
          </div>
          <div className="mt-2 text-[12px] text-slate-600" data-testid="phase1-carried-note">
            <span className="font-semibold text-slate-800">{carried}</span> assets were not AI-assessed in Cohort 1 and were carried forward to Cohort 2.
          </div>
          {p1Open && (
            <div className="mt-3 pt-3 border-t border-slate-200 space-y-2" data-testid="phase1-details">
              {cbTotal(p1.committee_breakdown) > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Committee status</span>
                  {['pending', 'under_review', 'scored'].map((k) => (
                    <button key={k} disabled={!admin}
                      onClick={() => click(1, `committee_${k}`, `Committee ${CB_LABELS[k]}`, p1.committee_breakdown[k])}
                      data-testid={`phase1-committee-${k}`}
                      className={`text-[11px] px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 ${admin ? 'hover:border-blue-400' : ''} ${activeCls(`1:committee_${k}`)}`}>
                      {CB_LABELS[k]} <span className="font-semibold">{p1.committee_breakdown[k]}</span>
                    </button>
                  ))}
                </div>
              )}
              {stg(p1, 5) > 0 && Object.keys(p1.availability_breakdown || {}).length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="phase1-availability-breakdown">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Availability Check · {stg(p1, 5)} Team Approved</span>
                  {Object.entries(p1.availability_breakdown).map(([k, v]) => (
                    <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">
                      {k} <span className="font-semibold">{v}</span>
                    </span>
                  ))}
                </div>
              )}
              {(p1.recommendations || []).length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">AI recommendations</span>
                  {p1.recommendations.map((r) => (
                    <button key={r.label} disabled={!admin}
                      onClick={() => click(1, `rec:${r.label}`, r.label, r.count)}
                      data-testid={`phase1-rec-${slug(r.label)}`}
                      className={`text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 ${admin ? 'hover:border-violet-400 hover:text-violet-700' : ''} ${activeCls(`1:rec:${r.label}`)}`}>
                      {r.label} <span className="font-semibold">{r.count}</span>
                    </button>
                  ))}
                  <button disabled={!admin}
                    onClick={() => click(1, 'carried', 'Carried Forward / Not AI Assessed', carried)}
                    data-testid="phase1-carried-chip"
                    className={`text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 ${admin ? 'hover:border-amber-500' : ''} ${activeCls('1:carried')}`}>
                    Carried Forward / Not AI Assessed <span className="font-semibold">{carried}</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        /* Fallback — Phase 1 still open (bootstrap couldn't reconcile). */
        <div className="border border-amber-300 rounded-lg bg-amber-50 p-3.5 text-[12px] text-amber-800" data-testid="phase1-open-fallback">
          Cohort 1 has not been closed yet ({stg(p1, 1)} eligible · {stg(p1, 2)} AI assessed · {p1.awaiting_ai} awaiting AI).
          {admin && (
            <button
              className="ml-2 text-[12px] font-semibold underline"
              data-testid="close-phase1-btn"
              onClick={async () => {
                try {
                  const pr = await axios.post(`${API}/viatris/journey/close-cohort`, { cohort_number: 1, confirm: false }, { withCredentials: true });
                  const s = pr.data.preview.summary;
                  if (!window.confirm(`Close Cohort 1 and carry ${s.awaiting_ai} Awaiting-AI assets into Cohort 2?\n\nEligible ${s.eligible} · AI assessed ${s.ai_assessed} · Committee ${s.committee} · Shortlisted ${s.shortlisted}`)) return;
                  const r = await axios.post(`${API}/viatris/journey/close-cohort`, { cohort_number: 1, confirm: true }, { withCredentials: true });
                  toast.success(`Cohort 1 completed — ${r.data.carried} assets carried into Cohort 2`);
                  await load(); onDataChanged && onDataChanged();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
              }}>
              Close Cohort 1 &amp; Start Cohort 2
            </button>
          )}
        </div>
      )}

      {/* ── Completed phases >= 2 (frozen snapshots, compact) ── */}
      {(phases || []).filter((p) => p.phase >= 2 && p.status === 'completed').map((cp) => (
        <div key={cp.phase} className="border border-slate-200 rounded-lg bg-slate-50/70 p-3.5" data-testid={`phase${cp.phase}-completed-card`}>
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[13px] font-bold text-slate-900">Cohort {cp.phase}</span>
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> Completed{cp.completed_at ? ` · ${cp.completed_at.slice(0, 10)}` : ''}
            </span>
            <div className="ml-auto"><ExportMenu phase={cp} /></div>
          </div>
          <div className="flex items-stretch gap-1.5 flex-wrap">
            {(cp.stages || []).map((s, i, arr) => (
              <React.Fragment key={s.stage}>
                <Stat phaseN={cp.phase} metric={s.metric || p2Metrics[s.stage]} count={s.count ?? 0} label={s.label || STAGE_LABELS[s.stage]} />
                {i < arr.length - 1 && <Arrow />}
              </React.Fragment>
            ))}
            <Arrow />
            <Stat phaseN={cp.phase} metric="responded" count={cp.responded_count ?? 0} label="Responded" />
          </div>
          {(cp.carried_forward ?? 0) > 0 && (
            <div className="mt-2 text-[12px] text-slate-600">
              <span className="font-semibold text-slate-800">{cp.carried_forward}</span> assets marked Carry Forward to Cohort {cp.phase + 1}.
            </div>
          )}
        </div>
      ))}

      {/* ── PHASE 2 — ACTIVE ── */}
      {active && (
        <div className="border-2 border-violet-300 rounded-lg bg-white p-4 shadow-sm" data-testid="phase2-active-card">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[14px] font-bold text-slate-900">Cohort {activeN}</span>
            <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5" data-testid="phase2-active-badge">Active</span>
            {admin && (
              <div className="ml-auto flex items-center gap-1.5">
                {(active.awaiting_ai ?? 0) > 0 && !job && (
                  <button onClick={runAI} data-testid="run-ai-phase2-btn"
                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 transition">
                    <Sparkles className="w-3.5 h-3.5" /> Run AI on {active.awaiting_ai} Assets
                  </button>
                )}
                <ExportMenu phase={active} />
                <button data-testid="close-active-phase-btn"
                  className="text-[11px] px-2.5 py-1 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50"
                  onClick={async () => {
                    try {
                      const r = await axios.post(`${API}/viatris/journey/close-cohort`, { cohort_number: activeN, confirm: false }, { withCredentials: true });
                      setCloser({ phase: activeN, preview: r.data.preview, carry: [] });
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Cannot close phase'); }
                  }}>Close Cohort {activeN}…</button>
              </div>
            )}
          </div>
          {(active.sources || []).length > 0 && (
            <div className="mb-1 text-[11px] text-slate-500" data-testid="phase-source-composition">
              <span className="font-semibold text-slate-600">Source:</span>{' '}
              {active.sources.map((s) => `${s.count} ${s.source}`).join(' · ')}
              {active.sources.length > 1 ? ` · Total ${active.sources.reduce((a, s) => a + s.count, 0)}` : ''}
            </div>
          )}
          <div className="text-[12px] text-slate-500 mb-3" data-testid="phase2-starting-population">
            {((active.sources || []).length <= 1)
              ? (<>Starting population: <span className="font-semibold text-slate-700">{stg(active, 1)}</span> assets carried forward from Cohort {activeN - 1} because they had not yet undergone AI assessment. These are a subset of the original Cohort {activeN - 1} eligible population — not new assets.</>)
              : (<>Starting population: <span className="font-semibold text-slate-700">{stg(active, 1)}</span> assets — composition shown above.</>)}
          </div>
          {stg(active, 1) > 0 && stg(active, 2) === stg(active, 1) && (
            <div className="mb-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1" data-testid="phase2-all-assessed-note">
              <CheckCircle2 className="w-3.5 h-3.5" /> All {stg(active, 1)} starting assets have completed AI assessment — Stage 1 and Stage 2 show the same {stg(active, 1)} assets progressing, not {stg(active, 1) * 2} assets.
            </div>
          )}
          {job && (
            <div className="mb-3 flex items-center gap-2 text-[12px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md px-3 py-2" data-testid="phase2-ai-progress">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              AI assessment running — {job.completed || 0}/{job.total || stg(active, 1)}
              {job.current_asset_name ? <span className="text-slate-500">· {job.current_asset_name}</span> : null}
            </div>
          )}
          <div className="flex items-stretch gap-1 flex-wrap" data-testid="phase2-stages">
            {(active.stages || []).map((s, i, arr) => (
              <React.Fragment key={s.stage}>
                <Stat big phaseN={activeN} metric={s.metric || p2Metrics[s.stage]} count={s.count ?? 0}
                  label={`Stage ${s.stage} · ${s.label || STAGE_LABELS[s.stage]}`} />
                {i < arr.length - 1 && <Arrow />}
              </React.Fragment>
            ))}
            <Arrow />
            <Stat big phaseN={activeN} metric="responded" count={active.responded_count ?? 0} label="Responded" />
          </div>
          {stg(active, 5) > 0 && Object.keys(active.availability_breakdown || {}).length > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="phase2-availability-breakdown">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Availability Check · {stg(active, 5)} Team Approved</span>
              {Object.entries(active.availability_breakdown).map(([k, v]) => (
                <span key={k} className="text-[11px] px-2 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">
                  {k} <span className="font-semibold">{v}</span>
                </span>
              ))}
            </div>
          )}
          {stg(active, 2) > 0 && (
            <div className="mt-2.5" data-testid="phase2-rec-distribution">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Stage 2 · AI Recommendation Distribution — {stg(active, 2)} assessed</span>
                {(active.recommendations || []).map((r) => (
                  <button key={r.label} disabled={!admin}
                    onClick={() => click(activeN, `rec:${r.label}`, r.label, r.count)}
                    data-testid={`phase2-rec-${slug(r.label)}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-white text-slate-600 ${admin ? 'hover:border-violet-400 hover:text-violet-700' : ''} ${activeCls(`${activeN}:rec:${r.label}`)}`}>
                    {r.label} <span className="font-semibold">{r.count}</span>
                  </button>
                ))}
                {(active.awaiting_ai ?? 0) > 0 && (
                  <button disabled={!admin}
                    onClick={() => click(activeN, 'awaiting_ai', 'Awaiting AI', active.awaiting_ai)}
                    data-testid="phase2-rec-awaiting-ai"
                    className={`text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-700 ${admin ? 'hover:border-amber-500' : ''} ${activeCls(`${activeN}:awaiting_ai`)}`}>
                    Awaiting AI <span className="font-semibold">{active.awaiting_ai}</span>
                  </button>
                )}
              </div>
              {admin && (
                <button onClick={openCommittee} data-testid="send-to-committee-btn"
                  className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border border-violet-300 text-violet-700 hover:bg-violet-50 transition">
                  <Users className="w-3.5 h-3.5" /> Send Selected to Committee Review
                </button>
              )}
            </div>
          )}
          {stg(active, 3) > 0 && (
            <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="phase2-committee-breakdown">
              <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">Stage 3 · Committee status</span>
              {['pending', 'under_review', 'scored'].map((k) => (
                <button key={k} disabled={!admin}
                  onClick={() => click(activeN, `committee_${k}`, `Committee ${CB_LABELS[k]}`, active.committee_breakdown[k])}
                  data-testid={`phase2-committee-${k}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 ${admin ? 'hover:border-blue-400' : ''} ${activeCls(`${activeN}:committee_${k}`)}`}>
                  {CB_LABELS[k]} <span className="font-semibold">{active.committee_breakdown[k]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Next Cohort Pool modal ── */}
      {poolModal && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => setPoolModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pool-modal">
            <div className="text-sm font-bold text-slate-900 mb-1">Next Cohort Pool — {poolAssets.length} assets</div>
            <div className="text-[11px] text-slate-500 mb-3">Eligible today, not yet assigned to any cohort. They join the next cohort via Create Next Cohort — or exceptionally, one can be added to the active cohort (audited).</div>
            <div className="space-y-1">
              {poolAssets.map((a) => (
                <div key={a.asset_id} className="flex items-center gap-2 text-[12px] text-slate-700 border-b border-slate-100 py-1">
                  {a.asset_name}
                  {active && (
                    <button className="ml-auto text-[10px] text-amber-700 border border-amber-300 rounded px-1.5 py-0.5 hover:bg-amber-50"
                      data-testid={`pool-add-to-phase-${a.asset_id}`}
                      onClick={async () => {
                        try {
                          const p = await axios.post(`${API}/viatris/journey/add-to-phase`, { asset_id: a.asset_id, confirm: false }, { withCredentials: true });
                          if (!window.confirm(`EXCEPTIONAL ACTION\n\n${p.data.warning}\n\nProceed?`)) return;
                          await axios.post(`${API}/viatris/journey/add-to-phase`, { asset_id: a.asset_id, confirm: true }, { withCredentials: true });
                          toast.success(`Added to Cohort ${p.data.phase_number} — recorded in the audit log`);
                          setPoolModal(false); await load(); onDataChanged && onDataChanged();
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                      }}>Add to Cohort {activeN} (exceptional)</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Create Next Phase builder ── */}
      {builder && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setBuilder(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="create-phase-modal">
            <div className="text-sm font-bold text-slate-900 mb-1">Create Cohort {builder.phase_number} — Builder</div>
            <div className="text-[11px] text-slate-500 mb-3">Select the sources. The snapshot freezes on creation.</div>
            {(builder.sources || []).map((s) => {
              const isCarry = s.source.startsWith('Carry');
              const checked = isCarry ? builder.include_carry : builder.include_pool;
              return (
                <label key={s.source} className="flex items-center gap-2 text-[12px] text-slate-700 py-1">
                  <input type="checkbox" checked={checked}
                    onChange={async (e) => {
                      const next = { include_carry: isCarry ? e.target.checked : builder.include_carry, include_pool: isCarry ? builder.include_pool : e.target.checked };
                      try {
                        const r = await axios.post(`${API}/viatris/journey/create-phase`, { confirm: false, ...next }, { withCredentials: true });
                        setBuilder({ ...r.data, ...next });
                      } catch { /* noop */ }
                    }} />
                  {s.source} <span className="font-semibold">— {s.count}</span>
                </label>
              );
            })}
            <div className="mt-2 text-[13px] font-semibold text-sky-700" data-testid="create-phase-total">Cohort {builder.phase_number} Starting Population: {builder.total} assets</div>
            <div className="mt-4 flex justify-end gap-2">
              <button className="text-[12px] px-3 py-1.5 rounded-md border border-slate-300" onClick={() => setBuilder(null)} disabled={busy}>Cancel</button>
              <button className="text-[12px] px-3 py-1.5 rounded-md bg-sky-600 text-white disabled:opacity-50" data-testid="create-phase-confirm-btn"
                disabled={busy || !builder.total}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await axios.post(`${API}/viatris/journey/create-phase`,
                      { confirm: true, include_carry: builder.include_carry, include_pool: builder.include_pool }, { withCredentials: true });
                    toast.success(`Cohort ${r.data.phase_number} created — ${r.data.total} assets frozen as the starting population`);
                    setBuilder(null); await load(); onDataChanged && onDataChanged();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
                  setBusy(false);
                }}>Create Cohort {builder.phase_number} — {builder.total} assets</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Close active phase (explicit carry-forward selection) ── */}
      {closer && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setCloser(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="close-phase-modal">
            <div className="text-sm font-bold text-slate-900 mb-1">Close Cohort {closer.phase}</div>
            <div className="text-[11px] text-slate-500 mb-3">
              The Cohort {closer.phase} snapshot freezes on closure. Tick the assets to CARRY FORWARD into Cohort {closer.phase + 1} — nothing carries automatically. Unticked assets are closed with this cohort.
            </div>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded p-2 space-y-0.5 mb-2">
              {(closer.preview?.rows || []).map((r) => (
                <label key={r.id} className="flex items-center gap-2 text-[11px] text-slate-600">
                  <input type="checkbox" checked={closer.carry.includes(r.id)}
                    onChange={(e) => setCloser({ ...closer, carry: e.target.checked ? [...closer.carry, r.id] : closer.carry.filter((x) => x !== r.id) })} />
                  {r.asset_name}
                  <span className="text-slate-400">{r.recommendation || 'Awaiting AI'}{r.committee_status ? ` · ${r.committee_status}` : ''}</span>
                </label>
              ))}
            </div>
            <div className="text-[12px] font-semibold text-violet-700 mb-3" data-testid="close-phase-carry-count">
              Carry forward to Cohort {closer.phase + 1}: {closer.carry.length} · Closed with Cohort {closer.phase}: {(closer.preview?.rows || []).length - closer.carry.length}
            </div>
            <div className="flex justify-end gap-2">
              <button className="text-[12px] px-3 py-1.5 rounded-md border border-slate-300" onClick={() => setCloser(null)} disabled={busy}>Cancel</button>
              <button className="text-[12px] px-3 py-1.5 rounded-md bg-slate-800 text-white disabled:opacity-50" data-testid="close-phase-confirm-btn"
                disabled={busy}
                onClick={async () => {
                  if (!window.confirm(`Close Cohort ${closer.phase}? Its results freeze permanently. ${closer.carry.length} asset(s) will be marked Carry Forward.`)) return;
                  setBusy(true);
                  try {
                    await axios.post(`${API}/viatris/journey/close-cohort`,
                      { cohort_number: closer.phase, confirm: true, carry_ids: closer.carry }, { withCredentials: true });
                    toast.success(`Cohort ${closer.phase} completed — ${closer.carry.length} asset(s) marked Carry Forward`);
                    setCloser(null); await load(); onDataChanged && onDataChanged();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Close failed'); }
                  setBusy(false);
                }}>Close Cohort {closer.phase}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send to Committee modal ── */}
      {modal === 'committee' && (
        <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && setModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-bold text-slate-900 mb-1">Send to Committee Review — Cohort {activeN}</div>
            <div className="text-[12px] text-slate-500 mb-3">Select AI recommendation categories and/or individual assets. Only your selection moves to Stage 3 — nothing is sent automatically.</div>
            <div className="space-y-1.5 mb-3">
              {[...new Set([...REC_CATEGORIES, ...candidates.map((a) => a.recommendation).filter(Boolean)])].map((c) => {
                const n = candidates.filter((a) => a.recommendation === c).length;
                return (
                  <label key={c} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <input type="checkbox" checked={cats.includes(c)}
                      data-testid={`committee-cat-${slug(c)}`}
                      onChange={(e) => previewCommittee(e.target.checked ? [...cats, c] : cats.filter((x) => x !== c), checked)} />
                    {c} <span className="text-[11px] text-slate-400">({n} available)</span>
                  </label>
                );
              })}
            </div>
            <button className="text-[11px] text-violet-700 underline mb-2" data-testid="committee-individual-toggle"
              onClick={() => setShowAssetPick((v) => !v)}>
              {showAssetPick ? 'Hide' : 'Select'} individual assets ({candidates.length})
            </button>
            {showAssetPick && (
              <div className="max-h-44 overflow-y-auto border border-slate-200 rounded p-2 space-y-0.5 mb-2" data-testid="committee-individual-list">
                {candidates.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-[11px] text-slate-600">
                    <input type="checkbox" checked={checked.includes(a.id)}
                      onChange={(e) => {
                        const next = e.target.checked ? [...checked, a.id] : checked.filter((x) => x !== a.id);
                        setChecked(next); previewCommittee(cats, next);
                      }} />
                    {a.asset_name} {a.recommendation ? <span className="text-slate-400">· {a.recommendation}</span> : null}
                  </label>
                ))}
                {candidates.length === 0 && <div className="text-[11px] text-slate-400">No assessed assets available for committee selection.</div>}
              </div>
            )}
            <div className="text-[13px] font-semibold text-violet-700 mb-3" data-testid="committee-preview-count">
              {preview ? `Selected for Committee Review: ${preview.selected_count} assets` : 'Calculating…'}
              {preview?.sample?.length > 0 && <div className="text-[11px] font-normal text-slate-400 mt-1">{preview.sample.slice(0, 6).join(', ')}{preview.selected_count > 6 ? '…' : ''}</div>}
            </div>
            <div className="flex justify-end gap-2">
              <button className="text-[12px] px-3 py-1.5 rounded-md border border-slate-300" onClick={() => setModal(null)} disabled={busy}>Cancel</button>
              <button className="text-[12px] px-3 py-1.5 rounded-md bg-violet-600 text-white disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="committee-confirm-btn"
                onClick={confirmCommittee} disabled={busy || !preview || preview.selected_count === 0}>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm — move {preview?.selected_count ?? 0} to Committee
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
